import { useAuth0 } from "@auth0/auth0-react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Button, Grid, Paper, TextField, Typography } from "@mui/material";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import PageHeader from "@/components/PageHeader";
import SuccessToast from "@/components/SuccessToast";
import { useUpdateOwnerMutation } from "@/mutations/owner.ts";
import { useQueryOptionsFactory } from "@/queries";
import type { Owner } from "@/queries/owners.ts";

const personalInfoSchema = z.object({
    ownerFirstName: z.string().min(1, "First name is required"),
    ownerName: z.string().min(1, "Last name is required"),
    email: z.string().email("Please enter a valid email address"),
    phone: z.string().optional(),
    phone2: z.string().optional(),
});

type PersonalInfoFormData = z.infer<typeof personalInfoSchema>;

export const Route = createFileRoute("/owner/")({
    component: RouteComponent,
});

function RouteComponent() {
    const { user } = useAuth0();

    if (!user?.email) {
        throw new Error("Auth0 user with no email.");
    }

    const qof = useQueryOptionsFactory();
    const pageData: { data: Owner } = useSuspenseQuery(qof.owners.get(user?.email));

    return DisplayComponent(pageData.data);
}

const DisplayComponent = (owner: Owner) => {
    const [showSuccessToast, setShowSuccessToast] = useState(false);
    const updateOwnerMutation = useUpdateOwnerMutation(owner.id);

    const inputStyles = {
        "& .MuiOutlinedInput-root": {
            backgroundColor: "#f8fafc",
            "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "#3b82f6",
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "#3b82f6",
            },
        },
    };
    const form = useForm<PersonalInfoFormData>({
        resolver: zodResolver(personalInfoSchema),
        defaultValues: {
            ownerFirstName: owner.ownerFirstName,
            ownerName: owner.ownerName,
            email: owner.email,
            phone: owner.phone,
            phone2: owner.phone2,
        },
    });
    const handleSubmit = (data: PersonalInfoFormData) => {
        updateOwnerMutation.mutate({ attributes: data, type: "owner" });
        setShowSuccessToast(true);
    };

    return (
        <Box sx={{ maxWidth: "100%" }}>
            <Paper elevation={2} sx={{ borderRadius: 3, overflow: "hidden" }}>
                <PageHeader
                    title={`Aloha ${owner.ownerFirstName} ${owner.ownerName},`}
                    instructions={[
                        "Edit your personal information",
                        "Update your contact details and unit information",
                        "All changes will be saved to the database",
                    ]}
                />

                <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, backgroundColor: "white" }}>
                    <Box
                        sx={{
                            mb: 4,
                            pb: 2,
                            borderBottom: "2px solid #e2e8f0",
                        }}
                    >
                        <Typography
                            variant="h5"
                            sx={{
                                fontWeight: 600,
                                color: "#1e293b",
                                mb: 1,
                                fontSize: { xs: "1.25rem", md: "1.5rem" },
                            }}
                        >
                            Edit Personal Information
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#64748b", fontSize: "0.9rem" }}>
                            Update your contact details and unit information
                        </Typography>
                    </Box>

                    <form onSubmit={form.handleSubmit(handleSubmit)}>
                        <Grid container spacing={{ xs: 2, sm: 3 }}>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Controller
                                    name="ownerFirstName"
                                    control={form.control}
                                    render={({ field, fieldState }) => (
                                        <TextField
                                            {...field}
                                            fullWidth
                                            label="First Name"
                                            variant="outlined"
                                            required
                                            error={!!fieldState.error}
                                            helperText={fieldState.error?.message}
                                            sx={inputStyles}
                                        />
                                    )}
                                />
                            </Grid>

                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Controller
                                    name="ownerName"
                                    control={form.control}
                                    render={({ field, fieldState }) => (
                                        <TextField
                                            {...field}
                                            fullWidth
                                            label="Last Name"
                                            variant="outlined"
                                            required
                                            error={!!fieldState.error}
                                            helperText={fieldState.error?.message}
                                            sx={inputStyles}
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        height: "56px",
                                        px: 2,
                                        backgroundColor: "#f8fafc",
                                        border: "1px solid #d1d5db",
                                        borderRadius: 1,
                                    }}
                                >
                                    <Typography
                                        variant="body2"
                                        sx={{ color: "#64748b", fontSize: "0.875rem", mr: 1 }}
                                    >
                                        Email:
                                    </Typography>
                                    <Typography
                                        sx={{ fontWeight: 500, color: "#1e293b", fontSize: "1rem" }}
                                    >
                                        {owner.email}
                                    </Typography>
                                </Box>
                            </Grid>

                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Controller
                                    name="phone"
                                    control={form.control}
                                    render={({ field, fieldState }) => (
                                        <TextField
                                            {...field}
                                            fullWidth
                                            label="Owner Phone 1"
                                            variant="outlined"
                                            error={!!fieldState.error}
                                            helperText={fieldState.error?.message}
                                            sx={inputStyles}
                                        />
                                    )}
                                />
                            </Grid>

                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Controller
                                    name="phone2"
                                    control={form.control}
                                    render={({ field, fieldState }) => (
                                        <TextField
                                            {...field}
                                            fullWidth
                                            label="Owner Phone 2"
                                            variant="outlined"
                                            error={!!fieldState.error}
                                            helperText={fieldState.error?.message}
                                            sx={inputStyles}
                                        />
                                    )}
                                />
                            </Grid>
                        </Grid>

                        <Box
                            sx={{
                                mt: 4,
                                pt: 3,
                                borderTop: "1px solid #e5e7eb",
                                display: "flex",
                                justifyContent: "center",
                            }}
                        >
                            <Button
                                type="submit"
                                variant="contained"
                                sx={{
                                    backgroundColor: "#22c55e",
                                    color: "white",
                                    px: { xs: 4, sm: 6 },
                                    py: 1.5,
                                    fontSize: { xs: "0.9rem", sm: "1rem" },
                                    fontWeight: 600,
                                    textTransform: "none",
                                    borderRadius: 2,
                                    boxShadow: "0 2px 8px rgba(34, 197, 94, 0.3)",
                                    width: { xs: "100%", sm: "auto" },
                                    maxWidth: { xs: "200px", sm: "none" },
                                    "&:hover": {
                                        backgroundColor: "#16a34a",
                                        boxShadow: "0 4px 12px rgba(34, 197, 94, 0.4)",
                                        transform: "translateY(-1px)",
                                    },
                                    transition: "all 0.2s ease-in-out",
                                }}
                            >
                                Save
                            </Button>
                        </Box>
                    </form>
                </Box>
            </Paper>

            <SuccessToast
                open={showSuccessToast}
                onClose={() => setShowSuccessToast(false)}
                title="Personal Information Updated!"
                message="Your information has been successfully saved."
            />
        </Box>
    );
};
